import styled from "styled-components";

export const Intro = styled.p`
  margin: 1rem 0;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-neutral-150);
`;

export const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  color: var(--color-neutral-150);

  button {
    font-weight: 700;
    color: var(--color-neutral-white);
    text-decoration: underline;
  }
`;

/** One frame per item (as /domains): radius 16, no border. */
export const List = styled.ul`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
  margin: 1rem 0 2rem;
  padding: 0;
  list-style: none;
`;

export const ItemCard = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  padding: 1rem 1.25rem;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  .body {
    flex: 1;
    min-width: 0;
  }

  h3 {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--color-neutral-white);
    overflow-wrap: anywhere;
  }

  p {
    margin-top: 0.125rem;
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

/** A pill: the main action in the brand's green, the others outlined. */
export const Pill = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  color: ${props => (props.$primary ? "var(--color-neutral-darkness)" : "var(--color-neutral-white)")};
  background: ${props => (props.$primary ? "var(--color-brand-plus)" : "transparent")};
  border: 1px solid
    ${props =>
      props.$primary ? "transparent" : props.$danger ? "var(--color-feedback-danger)" : "var(--color-neutral-light)"};
  cursor: pointer;

  &:hover {
    border-color: ${props => (props.$primary ? "transparent" : "var(--color-neutral-white)")};
  }

  &[aria-disabled="true"],
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

/** A switch with its text beside it, in a modal or a card. */
export const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.75rem 0;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  color: var(--color-neutral-white);
`;

export const FieldError = styled.p`
  margin-top: 0.375rem;
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  color: var(--color-feedback-danger);
`;

/** The agenda's menu: one frame per page (as /settings), its state under the title. */
export const MenuList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0;
  list-style: none;
`;

export const MenuCard = styled.button`
  display: flex;
  gap: 1rem;
  align-items: center;
  width: 100%;
  padding: 1rem 1.25rem;
  text-align: left;
  font-family: Inter, sans-serif;
  background: var(--color-neutral-dark);
  border: 1px solid transparent;
  border-radius: 1rem;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-neutral-base);
  }

  .icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 0.75rem;
  }

  .icon svg {
    width: 20px;
    height: 20px;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .title {
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .description {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  /* the status still loading: its line's place, pulsing */
  .status {
    width: 7rem;
    height: 0.75rem;
    margin-top: 0.375rem;
  }

  .chevron {
    color: var(--color-neutral-150);
  }
`;

/** How a part of the agenda is (as a Keepo's "● Ativo" on /sites): green on, orange waiting for the owner, grey off. */
export const MenuStatus = styled.span<{ $tone: "on" | "waiting" | "off" }>`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  color: ${({ $tone }) =>
    ({ on: "var(--color-feedback-success)", waiting: "#ffb066", off: "var(--color-neutral-150)" })[$tone]};

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    background-color: currentColor;
    border-radius: 100%;
  }
`;

/** The add button and the list under it, as "Create Keepo" on /sites: the same space between all of them. */
export const ListStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  > ul {
    margin: 0;
  }
`;

/** A section's title, and on its right small shortcuts. */
export const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  align-items: baseline;
  justify-content: space-between;

  .actions {
    display: flex;
    gap: 1rem;
  }

  button {
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-brand-plus);
    white-space: nowrap;
  }

  button:hover {
    text-decoration: underline;
  }
`;
